/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import FormControl from "@mui/material/FormControl";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ClearIcon from "@mui/icons-material/Close";

import { api, get, post } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosFilterBar, IosList, IosListItem, IosPage, IosSectionHeader, iosColors } from "mobile/components/IosParity";

const primaryActionSx = {
  bgcolor: "#B54708",
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#953A05", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "rgba(181,71,8,0.18)", color: "rgba(15,15,18,0.44)" },
};

const successActionSx = {
  bgcolor: iosColors.green,
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#0D6149", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "rgba(20,122,92,0.18)", color: "rgba(15,15,18,0.44)" },
};

const secondaryActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  borderColor: "rgba(181,71,8,0.38)",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)", borderColor: "rgba(181,71,8,0.52)" },
};

const dangerActionSx = {
  bgcolor: "rgba(215,38,30,0.07)",
  color: "#A51812",
  borderColor: "rgba(215,38,30,0.38)",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  "&:hover": { bgcolor: "rgba(215,38,30,0.12)", borderColor: "rgba(215,38,30,0.52)" },
};

const stickyFooterSx = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 20,
  px: 1,
  pt: 0.8,
  pb: "calc(env(safe-area-inset-bottom) + 0.8rem)",
  bgcolor: "rgba(247,248,250,0.96)",
  borderTop: `1px solid ${iosColors.line}`,
  backdropFilter: "blur(14px)",
};

const RETURN_POPULATE = {
  unit: { fields: ["documentId", "serialNumber", "optionalTag", "transmissionModel"] },
  requestedByCustomer: { fields: ["documentId", "name", "email", "phone"] },
  coreReturn: { fields: ["documentId", "status", "amount", "coreSerialNumber", "returnedAt"] },
  warrantyClaim: { fields: ["documentId", "rmaNumber", "status", "workflowStage"] },
};

const TYPE_OPTIONS = [
  { key: "all", label: "All", icon: "move_to_inbox", accent: "orange" },
  { key: "core_return", label: "Core", icon: "assignment_return", accent: "orange" },
  { key: "warranty_return", label: "Warranty", icon: "verified_user", accent: "green" },
];

const STATUS_OPTIONS = [
  { key: "all", label: "All", icon: "list_alt", accent: "orange" },
  { key: "needs_label", label: "Needs label", icon: "local_shipping", accent: "orange" },
  { key: "label_generated", label: "Label", icon: "receipt_long", accent: "green" },
  { key: "in_transit", label: "Transit", icon: "sync", accent: "green" },
  { key: "delivered", label: "Delivered", icon: "check_circle", accent: "green" },
  { key: "failed", label: "Exception", icon: "report_problem", accent: "red" },
];

const normalize = (row) => {
  if (!row) return null;
  if (row.data) return normalize(row.data);
  if (row.attributes) return { id: row.id, documentId: row.documentId || row.id, ...row.attributes };
  return { ...row, documentId: row.documentId || row.id };
};

const rowsOf = (value) => {
  if (Array.isArray(value)) return value.map(normalize).filter(Boolean);
  if (Array.isArray(value?.data)) return value.data.map(normalize).filter(Boolean);
  return [];
};

const pretty = (value, fallback = "-") => {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text.replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase());
};

const dateTimeText = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

const money = (amount, currency = "USD") => {
  const value = Number(amount || 0);
  const code = String(currency || "USD").trim().toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: code }).format(value);
  } catch {
    return `${code} ${value.toFixed(2)}`;
  }
};

const absoluteUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  const base = String(api?.defaults?.baseURL || "").trim();
  return base ? `${base}${raw}` : raw;
};

const rateKey = (rate) =>
  [
    String(rate?.quoteId || "").trim(),
    String(rate?.carrier || "").trim().toLowerCase(),
    String(rate?.service || "").trim().toLowerCase(),
    String(rate?.amount ?? rate?.total ?? "").trim(),
  ].join("|");

const requestLabel = (row) => (row?.requestType === "warranty_return" ? "Warranty return" : "Core return");

const unitLabel = (row) => {
  const unit = normalize(row?.unit);
  return unit?.optionalTag || unit?.serialNumber || row?.documentId || "-";
};

const compactAddress = (address) => {
  if (!address || typeof address !== "object") return "-";
  return [
    address.name || address.contactName || address.company,
    address.street1 || address.address1,
    address.street2 || address.address2,
    [address.city, address.state, address.zip || address.postalCode].filter(Boolean).join(", "),
    address.country,
  ]
    .filter(Boolean)
    .join(" • ");
};

const buildFilters = ({ search, type, status }) => {
  const and = [];
  if (type && type !== "all") and.push({ requestType: { $eq: type } });
  if (status === "needs_label") {
    and.push({
      $or: [
        { trackingNumber: { $null: true } },
        { shippingLabelUrl: { $null: true } },
        { shippingProviderStatus: { $eq: "pending" } },
      ],
    });
  } else if (status && status !== "all") {
    and.push({ shippingProviderStatus: { $eq: status } });
  }
  if (search) {
    and.push({
      $or: [
        { trackingNumber: { $containsi: search } },
        { shippingCarrier: { $containsi: search } },
        { unit: { serialNumber: { $containsi: search } } },
        { unit: { optionalTag: { $containsi: search } } },
        { requestedByCustomer: { name: { $containsi: search } } },
      ],
    });
  }
  return and.length ? { $and: and } : {};
};

export default function WarehouseShippingMobile() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("q") || "");
  const [submittedSearch, setSubmittedSearch] = useState(params.get("q") || "");
  const [type, setType] = useState(params.get("requestType") || "all");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [selectedId, setSelectedId] = useState(params.get("sourceId") || "");
  const [quote, setQuote] = useState(null);
  const [selectedRateKey, setSelectedRateKey] = useState("");
  const [bookingMode, setBookingMode] = useState("pickup_plus_label");
  const [notice, setNotice] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCarrier, setManualCarrier] = useState("");
  const [manualTracking, setManualTracking] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setSubmittedSearch(search.trim()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (selectedId) next.set("sourceId", selectedId);
    else next.delete("sourceId");
    if (submittedSearch) next.set("q", submittedSearch);
    else next.delete("q");
    if (type !== "all") next.set("requestType", type);
    else next.delete("requestType");
    if (status !== "all") next.set("status", status);
    else next.delete("status");
    setParams(next, { replace: true });
  }, [selectedId, submittedSearch, type, status]);

  const query = useQuery({
    queryKey: ["m-warehouse-shipping", submittedSearch, type, status],
    queryFn: () =>
      get("/api/valve-body-return-requests", {
        sort: "updatedAt:desc",
        populate: RETURN_POPULATE,
        filters: buildFilters({ search: submittedSearch, type, status }),
        pagination: { page: 1, pageSize: 50 },
      }),
    staleTime: 6_000,
  });

  const rows = useMemo(() => rowsOf(query.data?.data), [query.data]);

  useEffect(() => {
    if (selectedId || !rows.length) return;
    setSelectedId(String(rows[0].documentId || rows[0].id || ""));
  }, [rows, selectedId]);

  const selectedInList = rows.find((row) => String(row.documentId || row.id) === String(selectedId)) || null;
  const selectedQuery = useQuery({
    queryKey: ["m-warehouse-shipping-one", selectedId],
    queryFn: () => get(`/api/valve-body-return-requests/${selectedId}`, { populate: RETURN_POPULATE }),
    enabled: !!selectedId && !selectedInList,
    staleTime: 6_000,
  });

  const selected = selectedInList || normalize(selectedQuery.data?.data);

  useEffect(() => {
    setQuote(null);
    setSelectedRateKey("");
    setBookingMode(selected?.shippingBookingMode || "pickup_plus_label");
    setManualCarrier(selected?.shippingCarrier || "");
    setManualTracking(selected?.trackingNumber || "");
    setNotice(null);
  }, [selected?.documentId, selected?.shippingBookingMode, selected?.shippingCarrier, selected?.trackingNumber]);

  const requestDocumentId = selected?.documentId || selected?.id || "";
  const rates = Array.isArray(quote?.shipping?.rates) ? quote.shipping.rates : [];
  const selectedRate =
    rates.find((rate) => rateKey(rate) === selectedRateKey) ||
    (quote?.shipping?.selectedRate && rateKey(quote.shipping.selectedRate) === selectedRateKey ? quote.shipping.selectedRate : null);

  const refreshAll = async () => {
    await qc.invalidateQueries({ queryKey: ["m-warehouse-shipping"] });
    await qc.invalidateQueries({ queryKey: ["m-warehouse-shipping-one"] });
    await qc.invalidateQueries({ queryKey: ["m-core-returns-queue"] });
  };

  const quoteMutation = useMutation({
    mutationFn: () => post(`/api/storefront/staff/return-requests/${requestDocumentId}/shipping/quote`, { data: { bookingMode, debug: true } }),
    onSuccess: (response) => {
      const data = response?.data || response || {};
      const nextRates = Array.isArray(data?.shipping?.rates) ? data.shipping.rates : [];
      const preferred = data?.shipping?.selectedRate || nextRates[0] || null;
      setQuote(data);
      setSelectedRateKey(preferred ? rateKey(preferred) : "");
      setNotice({ type: nextRates.length ? "success" : "error", text: nextRates.length ? "Rates loaded." : "No carrier rates were returned." });
    },
    onError: (error) => setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to quote shipping." }),
  });

  const bookMutation = useMutation({
    mutationFn: () => {
      if (!selectedRate?.carrier || !selectedRate?.service) throw new Error("Select a rate before generating the label.");
      return post(`/api/storefront/staff/return-requests/${requestDocumentId}/shipping/retry`, {
        data: {
          bookingMode,
          carrier: selectedRate.carrier,
          service: selectedRate.service,
          quoteId: selectedRate.quoteId || null,
          packages: quote?.shipping?.packages || [],
          force: true,
        },
      });
    },
    onSuccess: async () => {
      setNotice({ type: "success", text: "Return label generated." });
      await refreshAll();
    },
    onError: (error) => setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to generate label." }),
  });

  const manualMutation = useMutation({
    mutationFn: () => {
      if (!manualCarrier.trim() || !manualTracking.trim()) throw new Error("Carrier and tracking number are required.");
      return post(`/api/storefront/staff/return-requests/${requestDocumentId}/shipping/manual`, {
        data: { carrier: manualCarrier.trim(), trackingNumber: manualTracking.trim() },
      });
    },
    onSuccess: async () => {
      setManualOpen(false);
      setNotice({ type: "success", text: "Manual tracking saved." });
      await refreshAll();
    },
    onError: (error) => setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to save tracking." }),
  });

  const cancelMutation = useMutation({
    mutationFn: () => post(`/api/storefront/staff/return-requests/${requestDocumentId}/shipping/cancel`, { data: {} }),
    onSuccess: async () => {
      setNotice({ type: "success", text: "Shipment cancelled." });
      await refreshAll();
    },
    onError: (error) => setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to cancel shipment." }),
  });

  const labelUrl = absoluteUrl(selected?.shippingLabelUrl);
  const trackUrl = absoluteUrl(selected?.shippingProviderTrackUrl);
  const customer = normalize(selected?.requestedByCustomer);
  const unit = normalize(selected?.unit);
  const coreReturn = normalize(selected?.coreReturn);
  const warrantyClaim = normalize(selected?.warrantyClaim);

  return (
    <IosPage>
      <MobileTopBar title="Shipping" subtitle="Inbound returns" onBack={() => navigate(-1)} />
      <Stack spacing={1.05} sx={{ mt: 1 }}>
        <IosSectionHeader eyebrow="Warehouse" title="Return shipping" subtitle="Quote labels, track inbound cores, and save customer-supplied tracking." />
        <IosFilterBar label="Type" value={type} options={TYPE_OPTIONS} onChange={setType} />
        <IosFilterBar label="Status" value={status} options={STATUS_OPTIONS} onChange={setStatus} />
        <TextField
          fullWidth
          placeholder="Search tracking, carrier, tag, serial"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small" sx={{ color: iosColors.subtle }}>search</Icon>
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  sx={{ color: "#8F3700", bgcolor: "rgba(255,106,0,0.07)", "&:hover": { bgcolor: "rgba(255,106,0,0.12)" } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {query.isLoading ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
        <IosList>
          {rows.map((row) => (
            <IosListItem
              key={row.documentId || row.id}
              icon={row.requestType === "warranty_return" ? "verified_user" : "assignment_return"}
              title={unitLabel(row)}
              meta={[
                requestLabel(row),
                normalize(row.requestedByCustomer)?.name || "No customer",
                row.shippingCarrier || "Carrier pending",
                row.trackingNumber || "Tracking pending",
              ].join(" • ")}
              trailing={pretty(row.shippingProviderStatus || row.status)}
              accent={row.shippingProviderStatus === "failed" ? "red" : row.trackingNumber ? "green" : "orange"}
              onClick={() => setSelectedId(String(row.documentId || row.id))}
            />
          ))}
          {!rows.length && !query.isLoading ? (
            <Typography sx={{ color: iosColors.muted, fontSize: 13, px: 1.2, py: 1.4 }}>No return shipments match this view.</Typography>
          ) : null}
        </IosList>

        {selectedQuery.isLoading ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
        {notice ? (
          <IosCard sx={{ borderColor: notice.type === "success" ? "rgba(20,122,92,0.35)" : "rgba(215,38,30,0.35)" }}>
            <Typography sx={{ color: notice.type === "success" ? iosColors.green : iosColors.red, fontSize: 13, fontWeight: 850 }}>{notice.text}</Typography>
          </IosCard>
        ) : null}

        {selected ? (
          <>
            <IosCard>
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <MetricChip label={pretty(selected.shippingProviderStatus || selected.status)} tone={selected.trackingNumber ? "green" : "orange"} />
                  <MetricChip label={requestLabel(selected)} tone={selected.requestType === "warranty_return" ? "green" : "orange"} />
                </Stack>
                <InfoRow label="Customer" value={customer?.name || "-"} />
                <InfoRow label="Unit" value={[unit?.optionalTag, unit?.serialNumber].filter(Boolean).join(" • ") || "-"} />
                <InfoRow label="Transmission" value={unit?.transmissionModel || "-"} />
                <InfoRow label="Core return" value={coreReturn?.documentId || coreReturn?.coreSerialNumber || "-"} />
                <InfoRow label="Warranty" value={warrantyClaim?.rmaNumber || warrantyClaim?.documentId || "-"} />
                <InfoRow label="Requested" value={dateTimeText(selected.requestedAt)} />
              </Stack>
            </IosCard>

            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.85 }}>Current shipment</Typography>
              <Stack spacing={0.65}>
                <InfoRow label="Carrier" value={selected.shippingCarrier || "-"} />
                <InfoRow label="Tracking" value={selected.trackingNumber || "-"} />
                <InfoRow label="Booking" value={pretty(selected.shippingBookingMode || bookingMode)} />
                <InfoRow label="Last sync" value={dateTimeText(selected.shippingProviderLastSyncAt)} />
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {labelUrl ? <Button sx={secondaryActionSx} onClick={() => window.open(labelUrl, "_blank", "noopener,noreferrer")}>Open label</Button> : null}
                  {trackUrl ? <Button sx={secondaryActionSx} onClick={() => window.open(trackUrl, "_blank", "noopener,noreferrer")}>Track</Button> : null}
                </Stack>
              </Stack>
            </IosCard>

            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.85 }}>Quote label</Typography>
              <Stack spacing={0.9}>
                <FormControl fullWidth size="small">
                  <InputLabel>Booking mode</InputLabel>
                  <Select label="Booking mode" value={bookingMode} onChange={(e) => setBookingMode(e.target.value)}>
                    <MenuItem value="pickup_plus_label">Pickup + label</MenuItem>
                    <MenuItem value="label_only">Label only</MenuItem>
                  </Select>
                </FormControl>
                <Button sx={primaryActionSx} disabled={quoteMutation.isPending || !requestDocumentId} onClick={() => quoteMutation.mutate()}>
                  {quoteMutation.isPending ? "Quoting..." : "Quote rates"}
                </Button>
                {quote ? (
                  <>
                    <Typography sx={{ color: iosColors.muted, fontSize: 12.5, lineHeight: 1.3 }}>
                      From: {compactAddress(quote?.shipping?.originAddress)}
                    </Typography>
                    <Typography sx={{ color: iosColors.muted, fontSize: 12.5, lineHeight: 1.3 }}>
                      To: {compactAddress(quote?.shipping?.destinationAddress)}
                    </Typography>
                    <Stack spacing={0.65}>
                      {rates.map((rate) => {
                        const key = rateKey(rate);
                        const selectedRateActive = selectedRateKey === key;
                        return (
                          <Button
                            key={key}
                            variant="outlined"
                            sx={{
                              justifyContent: "space-between",
                              borderRadius: 1,
                              borderColor: selectedRateActive ? iosColors.green : iosColors.line,
                              color: iosColors.ink,
                              bgcolor: selectedRateActive ? "rgba(20,122,92,0.08)" : iosColors.surface,
                              textAlign: "left",
                            }}
                            onClick={() => setSelectedRateKey(key)}
                          >
                            <span>{rate.carrier || "Carrier"} {rate.service ? `• ${rate.service}` : ""}</span>
                            <span>{money(rate.amount ?? rate.total ?? rate.cost, rate.currency)}</span>
                          </Button>
                        );
                      })}
                    </Stack>
                    <Button sx={successActionSx} disabled={!selectedRate || bookMutation.isPending} onClick={() => bookMutation.mutate()}>
                      {bookMutation.isPending ? "Generating..." : "Generate label"}
                    </Button>
                  </>
                ) : null}
              </Stack>
            </IosCard>

            <Stack direction="row" spacing={0.75}>
              <Button fullWidth sx={secondaryActionSx} onClick={() => setManualOpen(true)}>Manual tracking</Button>
              <Button
                fullWidth
                sx={dangerActionSx}
                disabled={!selected.trackingNumber || cancelMutation.isPending}
                onClick={() => {
                  if (window.confirm("Cancel this return shipment?")) cancelMutation.mutate();
                }}
              >
                {cancelMutation.isPending ? "Cancelling..." : "Cancel"}
              </Button>
            </Stack>
          </>
        ) : null}
      </Stack>

      <Dialog open={manualOpen} fullScreen onClose={() => (manualMutation.isPending ? null : setManualOpen(false))}>
        <IosPage>
          <MobileTopBar title="Manual tracking" subtitle={unitLabel(selected)} onBack={() => (manualMutation.isPending ? null : setManualOpen(false))} />
          <Stack spacing={1.05} sx={{ mt: 1, pb: 10 }}>
            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.85 }}>Carrier tracking</Typography>
              <Stack spacing={1}>
                <TextField fullWidth label="Carrier" value={manualCarrier} onChange={(e) => setManualCarrier(e.target.value)} />
                <TextField fullWidth label="Tracking number" value={manualTracking} onChange={(e) => setManualTracking(e.target.value)} />
              </Stack>
            </IosCard>
          </Stack>
          <Box sx={stickyFooterSx}>
            <Stack direction="row" spacing={0.75}>
              <Button fullWidth sx={secondaryActionSx} disabled={manualMutation.isPending} onClick={() => setManualOpen(false)}>Cancel</Button>
              <Button fullWidth sx={successActionSx} disabled={manualMutation.isPending} onClick={() => manualMutation.mutate()}>
                {manualMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </Box>
        </IosPage>
      </Dialog>
    </IosPage>
  );
}

function MetricChip({ label, tone = "orange" }) {
  const color = tone === "red" ? iosColors.red : tone === "green" ? iosColors.green : "#8F3700";
  const bg = tone === "red" ? "rgba(215,38,30,0.07)" : tone === "green" ? "rgba(20,122,92,0.08)" : "rgba(255,106,0,0.07)";
  return <Chip label={label} size="small" sx={{ bgcolor: bg, color, fontSize: 11.75, fontWeight: 850 }} />;
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
      <Typography sx={{ color: iosColors.muted, fontSize: 12.75, fontWeight: 850 }}>{label}</Typography>
      <Typography sx={{ color: iosColors.ink, fontSize: 12.75, fontWeight: 800, textAlign: "right", maxWidth: "62%" }}>{value || "-"}</Typography>
    </Stack>
  );
}
